import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { companyProfileAPI, officeSpaceAPI } from "../../services/meetingsAPI";
import { toast } from "react-toastify";

const createEmptyCompanyProfile = () => ({
  displayName: "",
  industry: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  description: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
});

const AddYourSpace = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  
  // Rooms state
  const [rooms, setRooms] = useState([]);
  
  // Form states for adding/editing room
  const [editingRoomIndex, setEditingRoomIndex] = useState(null);
  const [formData, setFormData] = useState({
    roomName: "",
    roomNumber: "",
    companyName: "",
    description: "",
    images: [],
    amenities: [],
    visibility: "private",
  });
  const [companyProfileLoading, setCompanyProfileLoading] = useState(false);
  const [companyProfileSaving, setCompanyProfileSaving] = useState(false);
  const [hasCompanyProfile, setHasCompanyProfile] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(createEmptyCompanyProfile());

  const isCompanyAdmin = currentUser?.role === "company_admin" && Boolean(currentUser?.company?._id || currentUser?.company);

  const availableAmenities = [
    "WiFi",
    "Projector",
    "AC",
    "Whiteboard",
    "Parking",
    "Video Conferencing",
    "Coffee/Tea",
    "Catering",
    "Sound System",
    "Natural Light"
  ];

  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          setIsAuthenticated(true);
        } else {
          setCurrentUser(null);
          setIsAuthenticated(false);
          navigate('/meeting-home');
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setCurrentUser(null);
        setIsAuthenticated(false);
        navigate('/meeting-home');
      }
    };

    checkAuth();
  }, [navigate]);

  // Fetch user's rooms
  useEffect(() => {
    const fetchUserRooms = async () => {
      if (!isAuthenticated || !currentUser) return;

      setIsLoadingRooms(true);
      try {
        const response = await officeSpaceAPI.getMyOfficeSpace();
        if (response.data.success && response.data.data) {
          const officeSpaceData = response.data.data;
          if (officeSpaceData.rooms && officeSpaceData.rooms.length > 0) {
            setRooms(officeSpaceData.rooms);
          } else {
            setRooms([]);
          }
        } else {
          setRooms([]);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setRooms([]);
        } else {
          console.error('Error fetching rooms:', error);
          toast.error('Failed to load your rooms');
        }
      } finally {
        setIsLoadingRooms(false);
      }
    };

    if (isAuthenticated && currentUser) {
      fetchUserRooms();
    }
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      if (!isAuthenticated || !currentUser || !isCompanyAdmin) {
        setHasCompanyProfile(false);
        setCompanyProfile(createEmptyCompanyProfile());
        return;
      }

      setCompanyProfileLoading(true);
      try {
        const response = await companyProfileAPI.getMyCompanyProfile();
        const payload = response?.data?.data || {};
        const incomingProfile = payload.profile || {};

        setHasCompanyProfile(Boolean(payload.hasProfile));
        setCompanyProfile({
          ...createEmptyCompanyProfile(),
          ...incomingProfile,
          displayName: incomingProfile.displayName || payload.name || currentUser?.company?.name || "",
        });
      } catch (error) {
        console.error("Error fetching company profile:", error);
        toast.error(error.response?.data?.message || "Failed to load company profile");
      } finally {
        setCompanyProfileLoading(false);
      }
    };

    fetchCompanyProfile();
  }, [isAuthenticated, currentUser, isCompanyAdmin]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleCompanyProfileChange = (field, value) => {
    setCompanyProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveCompanyProfile = async (e) => {
    e.preventDefault();

    if (!isCompanyAdmin) {
      return;
    }

    if (!hasCompanyProfile && !companyProfile.displayName.trim()) {
      toast.error("Company name is required to create profile");
      return;
    }

    const payload = {
      displayName: companyProfile.displayName.trim(),
      industry: companyProfile.industry.trim(),
      website: companyProfile.website.trim(),
      contactEmail: companyProfile.contactEmail.trim(),
      contactPhone: companyProfile.contactPhone.trim(),
      description: companyProfile.description.trim(),
      addressLine1: companyProfile.addressLine1.trim(),
      addressLine2: companyProfile.addressLine2.trim(),
      city: companyProfile.city.trim(),
      state: companyProfile.state.trim(),
      country: companyProfile.country.trim(),
      postalCode: companyProfile.postalCode.trim(),
    };

    setCompanyProfileSaving(true);
    try {
      const response = hasCompanyProfile
        ? await companyProfileAPI.updateCompanyProfile(payload)
        : await companyProfileAPI.createCompanyProfile(payload);

      const result = response?.data?.data || {};
      const nextProfile = result.profile || payload;

      setHasCompanyProfile(Boolean(result.hasProfile));
      setCompanyProfile({
        ...createEmptyCompanyProfile(),
        ...nextProfile,
        displayName: nextProfile.displayName || result.name || currentUser?.company?.name || "",
      });

      toast.success(response?.data?.message || "Company profile saved successfully");
    } catch (error) {
      console.error("Error saving company profile:", error);
      toast.error(error.response?.data?.message || "Failed to save company profile");
    } finally {
      setCompanyProfileSaving(false);
    }
  };


  const resetForm = () => {
    setFormData({
      roomName: "",
      roomNumber: "",
      companyName: "",
      description: "",
      images: [],
      amenities: [],
      visibility: "private",
    });
    setEditingRoomIndex(null);
  };

  const handleEdit = (index) => {
    const room = rooms[index];
    setFormData({
      roomName: room.roomName || "",
      roomNumber: room.roomNumber || "",
      companyName: room.companyName || "",
      description: room.description || "",
      images: room.images || [],
      amenities: room.amenities || [],
      visibility: room.visibility || "private",
    });
    setEditingRoomIndex(index);
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this room?')) {
      return;
    }

    const updatedRooms = rooms.filter((_, i) => i !== index);
    await saveRooms(updatedRooms);
  };

  const saveRooms = async (roomsToSave) => {
    if (!isAuthenticated) {
      toast.error('Please login to save rooms');
      return;
    }

    if (roomsToSave.length === 0 || roomsToSave.some(r => !r.roomName || r.roomName.trim() === '')) {
      toast.error('Please enter at least one room with a name');
      return;
    }

    setLoading(true);
    try {
      const officeSpaceData = {
        rooms: roomsToSave.map(room => ({
          roomName: room.roomName.trim(),
          roomNumber: room.roomNumber?.trim() || '',
          companyName: room.companyName?.trim() || '',
          description: room.description || '',
          images: room.images || [],
          amenities: room.amenities || [],
          visibility: room.visibility === "public" ? "public" : "private",
        }))
      };

      const response = await officeSpaceAPI.createOrUpdateOfficeSpace(officeSpaceData);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Rooms saved successfully!');
        setRooms(roomsToSave);
        resetForm();
      } else {
        toast.error(response.data.message || 'Failed to save rooms');
      }
    } catch (error) {
      console.error('Error saving rooms:', error);
      toast.error(error.response?.data?.message || 'Failed to save rooms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.roomName || formData.roomName.trim() === '') {
      toast.error('Please enter a room name');
      return;
    }

    let updatedRooms;
    if (editingRoomIndex !== null) {
      // Update existing room
      updatedRooms = rooms.map((room, index) =>
        index === editingRoomIndex
          ? {
              roomName: formData.roomName.trim(),
              roomNumber: formData.roomNumber.trim(),
              companyName: formData.companyName.trim(),
              description: formData.description.trim(),
              images: formData.images || [],
              amenities: formData.amenities,
              visibility: formData.visibility === "public" ? "public" : "private",
            }
          : room
      );
    } else {
      // Add new room
      updatedRooms = [
        ...rooms,
        {
          roomName: formData.roomName.trim(),
          roomNumber: formData.roomNumber.trim(),
          companyName: formData.companyName.trim(),
          description: formData.description.trim(),
          images: formData.images || [],
          amenities: formData.amenities,
          visibility: formData.visibility === "public" ? "public" : "private",
        }
      ];
    }

    await saveRooms(updatedRooms);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen font-sans text-gray-800 antialiased bg-gradient-to-br from-blue-50 via-white to-indigo-50 page-content">
      <Navbar isScrolled={false} showLoginModal={true} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Add Your Space
            </h1>
            <p className="text-lg text-gray-600">
              {isCompanyAdmin
                ? "Manage your company profile and office meeting rooms. Company admins can create the profile once and edit it any time."
                : "Manage your office meeting rooms. Add, edit, or remove rooms that only you can see and book."}
            </p>
          </div>

          {isCompanyAdmin && (
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 mb-8">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Company Profile</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Add your company profile the first time, then update it whenever needed.
                  </p>
                </div>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${hasCompanyProfile ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {hasCompanyProfile ? "Profile Created" : "Profile Not Created"}
                </span>
              </div>

              {companyProfileLoading ? (
                <p className="text-gray-600">Loading company profile...</p>
              ) : (
                <form onSubmit={handleSaveCompanyProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Display Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={companyProfile.displayName}
                        onChange={(e) => handleCompanyProfileChange("displayName", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="e.g., ABC Corporation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                      <input
                        type="text"
                        value={companyProfile.industry}
                        onChange={(e) => handleCompanyProfileChange("industry", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="e.g., IT Services"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Website</label>
                      <input
                        type="text"
                        value={companyProfile.website}
                        onChange={(e) => handleCompanyProfileChange("website", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="https://example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Email</label>
                      <input
                        type="email"
                        value={companyProfile.contactEmail}
                        onChange={(e) => handleCompanyProfileChange("contactEmail", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="contact@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Phone</label>
                      <input
                        type="text"
                        value={companyProfile.contactPhone}
                        onChange={(e) => handleCompanyProfileChange("contactPhone", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Postal Code</label>
                      <input
                        type="text"
                        value={companyProfile.postalCode}
                        onChange={(e) => handleCompanyProfileChange("postalCode", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="560001"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Description</label>
                    <textarea
                      value={companyProfile.description}
                      onChange={(e) => handleCompanyProfileChange("description", e.target.value)}
                      rows="3"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700 resize-none"
                      placeholder="Write a short profile summary for your company..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Address Line 1</label>
                      <input
                        type="text"
                        value={companyProfile.addressLine1}
                        onChange={(e) => handleCompanyProfileChange("addressLine1", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="Building / Street"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Address Line 2</label>
                      <input
                        type="text"
                        value={companyProfile.addressLine2}
                        onChange={(e) => handleCompanyProfileChange("addressLine2", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="Area / Landmark"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={companyProfile.city}
                        onChange={(e) => handleCompanyProfileChange("city", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={companyProfile.state}
                        onChange={(e) => handleCompanyProfileChange("state", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                      <input
                        type="text"
                        value={companyProfile.country}
                        onChange={(e) => handleCompanyProfileChange("country", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={companyProfileSaving}
                      className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {companyProfileSaving
                        ? "Saving..."
                        : hasCompanyProfile
                          ? "Update Company Profile"
                          : "Create Company Profile"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Add/Edit Room Form */}
          <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingRoomIndex !== null ? 'Edit Room' : 'Add New Room'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Room Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Room Name / Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.roomName}
                    onChange={(e) => handleInputChange('roomName', e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                    placeholder="e.g., Conference Room A, Room 101"
                  />
                </div>

                {/* Room Number (Optional) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Room Number <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.roomNumber}
                    onChange={(e) => handleInputChange('roomNumber', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                    placeholder="e.g., 101, A-205"
                  />
                </div>
              </div>

              {/* Company/Office Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name / Office Name <span className="text-gray-500 text-xs">(Compolsory)</span>
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                  placeholder="e.g., ABC Corporation, Tech Park Office"
                />
              </div>

              {/* Room Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Room Description <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700 resize-none"
                  placeholder="Describe this room..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Room Visibility <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => handleInputChange("visibility", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700"
                >
                  <option value="private">Private (only your company can book)</option>
                  <option value="public">Public (all registered companies can book)</option>
                </select>
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Amenities
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {availableAmenities.map((amenity) => (
                    <label
                      key={amenity}
                      className="flex items-center p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.amenities.includes(amenity)}
                        onChange={() => handleAmenityToggle(amenity)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                      />
                      <span className="text-gray-700 font-medium text-sm">{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-4 pt-4">
                {editingRoomIndex !== null && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-bold text-lg rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : editingRoomIndex !== null ? 'Update Room' : 'Add Room'}
                </button>
              </div>
            </form>
          </div>

          {/* Rooms List */}
          <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Your Rooms ({rooms.length})
            </h2>

            {isLoadingRooms ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-6 animate-spin">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
                </div>
                <p className="text-gray-600 font-medium">Loading your rooms...</p>
              </div>
            ) : rooms.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg font-medium mb-2">No rooms added yet</p>
                <p className="text-gray-400 text-sm">Add your first room using the form above</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rooms.map((room, index) => (
                  <div
                    key={index}
                    className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {room.roomName}
                        </h3>
                        {room.roomNumber && (
                          <p className="text-sm text-gray-500">Room #{room.roomNumber}</p>
                        )}
                        {room.companyName && (
                          <p className="text-sm text-blue-600 font-medium">
                            {room.companyName}
                          </p>
                        )}
                        <p className={`mt-1 inline-flex px-2 py-1 rounded-full text-xs font-semibold ${room.visibility === "public" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                          {room.visibility === "public" ? "Public Room" : "Private Room"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(index)}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                          title="Edit Room"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                          title="Delete Room"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {room.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {room.description}
                      </p>
                    )}

                    {room.amenities && room.amenities.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2">Amenities:</p>
                        <div className="flex flex-wrap gap-2">
                          {room.amenities.map((amenity, amenityIndex) => (
                            <span
                              key={amenityIndex}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddYourSpace;

